import { useLayoutEffect, useRef, useState } from "react";
import { Card } from "@/types/v1";
import { ClockIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ClassNameValue } from "tailwind-merge";
import { Tooltip } from "@/components/Common/Tooltip";

export type JobHourDisplayProps = Pick<Card, "totalLaborHours"> & {
  isManualJobHoursEnabled?: boolean;
  isManualCardOnly?: boolean;
  className?: ClassNameValue;
  iconSize?: number;
  /**
   * Footer-density rendering: no clock icon and a dark pill that adapts to the
   * room it is given - the exact "completed/estimated" (10.5/20.5) when there
   * is room, the remaining hours (10h) when tight, never hidden -
   * with a tooltip spelling out the numbers. Off by default so the details
   * view, metrics dialog and container footer are unchanged.
   */
  compact?: boolean;
};

const roundToTenth = (value?: number) => Math.round((value ?? 0) * 10) / 10;

/**
 * Whether the completed side is shown. Integrated shops always have one (time
 * clock); a manual-only card shows it only once someone has entered completed
 * hours, so an untouched 0 doesn't turn "5h" into a noisy "0/5".
 */
const shouldShowLogged = (
  isManualCardOnly: boolean,
  loggedLabor?: number
): boolean => !isManualCardOnly || (loggedLabor ?? 0) > 0;

type HoursMode = "exact" | "short";

// The pill's box height is its line-height (16px at most zoom tiers, 12px at
// "emphasize" - see Card.css) plus no vertical padding, so any radius >= half
// that height renders identically to rounded-full (CSS's corner-overlap
// clamp). 4px stays well under 6px (half of the smallest tier) so the corners
// are visibly less round at every tier, matching Card.TechSALabel's badges.
const PILL_CLASSES =
  "card-text whitespace-nowrap rounded-[4px] bg-[#3f3f46] px-0.5 font-semibold text-white";

type CompactJobHoursProps = Pick<
  JobHourDisplayProps,
  "totalLaborHours" | "isManualCardOnly" | "className"
>;

/**
 * The footer pill. It owns a flex slot (flex-1 min-w-0, right-aligned) whose
 * width is exactly the space left after the timer - it does not depend on the
 * pill, so switching forms can never re-trigger the observer. Two invisible
 * absolute measurers (text drawn via ::after so they add no DOM text) give each
 * form's natural width; the slot's width picks exact -> short. The slot has a
 * min-width of the short form, so the pill is always shown.
 */
const CompactJobHours = ({
  totalLaborHours,
  isManualCardOnly = false,
  className,
}: CompactJobHoursProps) => {
  const logged = roundToTenth(totalLaborHours?.loggedLabor);
  const estimated = roundToTenth(totalLaborHours?.estimatedLabor);
  const showLogged = shouldShowLogged(
    isManualCardOnly,
    totalLaborHours?.loggedLabor
  );
  // Remaining comes from the raw values (rounding each side first can be off
  // by a tenth). A manual-only card with no completed hours has no logged
  // side. `${-0}` prints "0", so an over-budget sliver that rounds to -0 shows
  // "0h", never "-0h".
  const remaining = roundToTenth(
    (totalLaborHours?.estimatedLabor ?? 0) -
      (showLogged ? (totalLaborHours?.loggedLabor ?? 0) : 0)
  );
  const shortText = `${remaining}h`;
  const exactText = showLogged ? `${logged}/${estimated}` : shortText;

  const slotRef = useRef<HTMLDivElement>(null);
  const exactRef = useRef<HTMLSpanElement>(null);
  const shortRef = useRef<HTMLSpanElement>(null);
  // Exact until measured: with no layout (first paint, jsdom) all widths are 0.
  const [mode, setMode] = useState<HoursMode>("exact");
  // The short form's width. The slot never shrinks below it (inline min-width
  // on the slot), so when the footer is too narrow flexbox takes the shortfall
  // from the timer instead. The hours are never hidden.
  const [floor, setFloor] = useState(0);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    const exactEl = exactRef.current;
    const shortEl = shortRef.current;
    if (!slot || !exactEl || !shortEl) return;
    const fit = () => {
      setFloor(shortEl.offsetWidth);
      setMode(slot.clientWidth >= exactEl.offsetWidth ? "exact" : "short");
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    // Observe the slot AND the two measurers, never the pill: the card is scaled
    // with CSS zoom and `.card-text` letter-spacing changes per zoom tier, so the
    // text widths can change while the slot's width does not. A measurer's width
    // depends only on its own text/font - not on the pill or the slot - so this
    // cannot feed back into the observer.
    const observer = new ResizeObserver(fit);
    observer.observe(slot);
    observer.observe(exactEl);
    observer.observe(shortEl);
    return () => observer.disconnect();
  }, [exactText, shortText]);

  const measurerClasses = cn(
    "pointer-events-none invisible absolute left-0 top-0 after:content-[attr(data-text)]",
    PILL_CLASSES,
    className
  );

  return (
    <div
      ref={slotRef}
      data-hours-slot=""
      style={{ minWidth: floor || undefined }}
      className="relative flex min-w-0 flex-1 items-center justify-end overflow-hidden"
    >
      <span
        ref={exactRef}
        aria-hidden="true"
        data-hours-measure="exact"
        data-text={exactText}
        className={measurerClasses}
      />
      <span
        ref={shortRef}
        aria-hidden="true"
        data-hours-measure="short"
        data-text={shortText}
        className={measurerClasses}
      />
      <Tooltip
        delayDuration={150}
        message={
          showLogged
            ? `Job hours: ${logged} completed of ${estimated} estimated`
            : `Job hours: ${estimated} estimated`
        }
      >
        <div
          data-hours-pill=""
          data-mode={mode}
          className={cn("shrink-0 cursor-default", PILL_CLASSES, className)}
        >
          {mode === "exact" ? exactText : shortText}
        </div>
      </Tooltip>
    </div>
  );
};

export const JobHourDisplay = ({
  totalLaborHours,
  isManualJobHoursEnabled = false,
  isManualCardOnly = false,
  className,
  iconSize = 14,
  compact = false,
}: JobHourDisplayProps) => {
  if (!isManualJobHoursEnabled && isManualCardOnly) return null;

  if (compact) {
    return (
      <CompactJobHours
        totalLaborHours={totalLaborHours}
        isManualCardOnly={isManualCardOnly}
        className={className}
      />
    );
  }

  return (
    <div
      className={cn(
        "card-text flex flex-row items-center justify-center gap-1 text-sm",
        className
      )}
    >
      <ClockIcon size={iconSize} />
      {shouldShowLogged(isManualCardOnly, totalLaborHours?.loggedLabor) ? (
        <p>{`${roundToTenth(totalLaborHours?.loggedLabor)}/${roundToTenth(totalLaborHours?.estimatedLabor)}`}</p>
      ) : (
        <p className="ml-1">{`${roundToTenth(totalLaborHours?.estimatedLabor)}`}</p>
      )}
    </div>
  );
};
